/* eslint-disable no-underscore-dangle */
import { useCallback, useMemo } from 'react';
import { ServiceCategory } from '@prisma/client';
import { useTheme } from '@mui/material';
import { FormData, InputRow } from '../../global/FormSmallModal';
import {
  BusinessService,
  ONLINE_BOOKINGS_BEHAVIOR,
  UpsertBusinessServiceInitValues,
  upsertBusinessServiceFormSchema,
} from '../../../types/businesses';
import {
  getBusinessCurrency,
  handleUpsertBusinessService,
} from '../../../helpers/businesses';
import { LoadedUser } from '../../../interfaces';
import useUser from '../../../hooks/global/useUser';
import useGetWaivers from '../../../hooks/waivers/useGetWaivers';
import useGetOnboarding from '../../../hooks/businesses/useGetOnboarding';
import usePiercingLocations from '../../../hooks/piercings/usePiercingLocations';
import useFeatureGuard from '../../../hooks/subscriptions/useFeatureGuard';
import { Feature } from '../../../types/subscriptions';
import { postMessageToReactNative } from '../../../utils/global/helper';
import useConfirm from '../../../hooks/useConfirm';

const TOOLTIP: Record<string, string> = {
  name: 'Service name will be visible to customers.',
  description: 'Service description will be visible to customers.',
  defaultPrice:
    'You can always change the price of each individual appointment but this will give clients an estimate of where prices start at for this service.',
  defaultDepositPrice:
    'This is the default deposit price for this service. You can always change the deposit price of each individual appointment.',
  defaultDurationInMinutes:
    'This is the default duration for this service. You can always change the duration of each individual appointment. Default duration must be in increments of 5 minutes.',
  onlineBookingsBehavior:
    'Choose how you want customers to book their appointments for this service online.',
  enableCheckIn:
    'Allow customers to select this service when checking into your walk-in queue.',
  aftercareLink:
    'Add an aftercare link to send customers after an appointment for this service is completed',
  serviceCategory:
    'This can only be changed as long as no appointments have been created for this service.',
  requireMinorIdentification:
    'If this service is offered to minors, would you like to require them to upload their ID?',
  requireMinorBirthCertificate:
    'If this service is offered to minors, would you like to require them to upload their birth certificate?',
  requireMinorGuardianIdentification:
    'If this service is offered to minors, would you like to require the parent/guardian to upload their identification?',
  requireMinorGuardianPresent:
    'If this service is offered to minors, would you like to require the parent/guardian to be present at the time of the appointment?',
};

interface UseServiceFormOptions {
  refetch: () => Promise<unknown>;
  refetchWaivers: () => Promise<unknown>;
  setForm: (form: FormData | null) => void;
  onCustomizeBookingForm: (service: BusinessService | null) => void;
  getPendingIntakeQuestions?: () => { questionId: string; enabled: boolean }[];
}

function useServiceForm({
  refetch,
  refetchWaivers,
  setForm,
  onCustomizeBookingForm,
  getPendingIntakeQuestions,
}: UseServiceFormOptions) {
  const confirm = useConfirm();
  const { user, reloadUser } = useUser<LoadedUser>();
  const { data: waivers } = useGetWaivers();
  const { reloadAfterUpdate } = useGetOnboarding();
  const rawPiercingLocations = usePiercingLocations();
  const theme = useTheme();
  const hasWaiversFeature = useFeatureGuard(Feature.WAIVERS);

  const piercingLocationsOptions = useMemo(
    () =>
      (rawPiercingLocations ?? []).map(location => ({
        label: location.locationType,
        value: location.locationId,
        category: location.locationCategory,
      })),
    [rawPiercingLocations],
  );

  const getServiceForm = useCallback(
    (
      serviceToUpdate?: BusinessService | null,
    ): FormData<UpsertBusinessServiceInitValues> => {
      const draftWaivers = waivers?.filter(waiver => waiver.status === 'DRAFT');
      const waiverName = serviceToUpdate?.waiver?.name ?? '';

      const minimumAgeInMonths = serviceToUpdate?.minimumAgeInMonths ?? 0;
      const isYears = minimumAgeInMonths % 12 === 0;
      const unit = isYears || !minimumAgeInMonths ? 'years' : 'months';
      const minimumAge =
        unit === 'months'
          ? minimumAgeInMonths
          : parseInt((minimumAgeInMonths / 12).toString(), 10);

      const isExistingServicePiercing =
        serviceToUpdate?.serviceCategory === ServiceCategory.PIERCING;
      const piercingLocations = isExistingServicePiercing
        ? (serviceToUpdate?.piercingLocations ?? [])?.map(pl => pl.locationId)
        : [];

      const getEtbRate = (e: (typeof user.business.employees)[number]) => {
        if (e.payrollType === 'COMMISSION') {
          return (e.paymentToEmployeePercent ?? 0) * 100;
        }
        if (e.payrollType === 'RENT') return 100;
        if (e.payrollType === 'WAGE') return 0;
        return e.payeeForOtherPayroll === 'EMPLOYEE' ? 100 : 0;
      };

      const employeeCommissions = (user?.business?.employees ?? []).map(e => {
        const ets = serviceToUpdate?.employeesToServices?.find(
          s => s.employeeId === e.employeeId,
        );
        const hasCustomRate =
          e.payrollType === 'COMMISSION' &&
          ets?.serviceCommissionRate != null &&
          ets.serviceCommissionRate >= 0;
        return {
          employeeId: e.employeeId,
          enabled: hasCustomRate,
          rate: hasCustomRate
            ? parseFloat(
                ((ets!.serviceCommissionRate as number) * 100).toFixed(1),
              )
            : getEtbRate(e),
        };
      });

      const initialValues: UpsertBusinessServiceInitValues = {
        serviceCategory: (serviceToUpdate?.serviceCategory ||
          '') as ServiceCategory,
        name: serviceToUpdate?.serviceName || '',
        description: serviceToUpdate?.serviceDescription || '',
        defaultDurationInMinutes:
          serviceToUpdate?.defaultDurationInMinutes || 0,
        defaultPrice: serviceToUpdate?.defaultPrice || 0,
        defaultDepositPrice: serviceToUpdate?.defaultDepositPrice || 0,
        employeeIds:
          serviceToUpdate?.employeesToServices?.map(e => e.employeeId) || [],
        onlineBookingsBehavior: serviceToUpdate
          ? serviceToUpdate.enableDirectBookings
            ? ONLINE_BOOKINGS_BEHAVIOR.DIRECT_BOOKINGS
            : serviceToUpdate.enableBookingRequests
            ? ONLINE_BOOKINGS_BEHAVIOR.BOOKING_REQUESTS
            : ONLINE_BOOKINGS_BEHAVIOR.NOT_ONLINE
          : ONLINE_BOOKINGS_BEHAVIOR.DIRECT_BOOKINGS,
        enableCheckIn: serviceToUpdate?.enableCheckIn ?? true,
        aftercareLink: serviceToUpdate?.aftercareLink || '',
        minimumAge,
        minimumAgeUnit: unit,
        requireMinorIdentification:
          serviceToUpdate?.requireMinorIdentification ?? true,
        requireMinorBirthCertificate:
          serviceToUpdate?.requireMinorBirthCertificate ?? true,
        requireMinorGuardianIdentification:
          serviceToUpdate?.requireMinorGuardianIdentification ?? true,
        requireMinorGuardianPresent:
          serviceToUpdate?.requireMinorGuardianPresent ?? true,
        isGeneralPiercings: (serviceToUpdate
          ? isExistingServicePiercing && piercingLocations.length > 1
          : undefined) as boolean,
        waiverName: waiverName || '',
        piercingLocations,
        employeeCommissions,
      };

      return {
        initialValues,
        inputRows: (values, { setFieldValue }) => {
          const isPiercing =
            values.serviceCategory === ServiceCategory.PIERCING;
          const serviceAgeInMonths =
            values.minimumAgeUnit === 'years'
              ? values.minimumAge * 12
              : values.minimumAge;
          const isMinorAge = serviceAgeInMonths < 18 * 12;
          const piercingRows: InputRow<UpsertBusinessServiceInitValues>[] =
            isPiercing
              ? [
                  [
                    {
                      inputType: 'select',
                      label:
                        'Is this a general piercing service, or specific to an individual piercing location?',
                      isLabelMultiline: true,
                      name: 'isGeneralPiercings',
                      tooltip: TOOLTIP.isGeneralPiercings,
                      placeholder: 'Select an option',
                      options: [
                        { label: 'General piercing service', value: true },
                        {
                          label: 'Specific to an individual piercing location',
                          value: false,
                        },
                      ],
                      required: true,
                      autoWidth: false,
                      onChange: newIsGeneralPiercings => {
                        const newPiercingLocations = newIsGeneralPiercings
                          ? piercingLocationsOptions.map(option => option.value)
                          : [];
                        setFieldValue(
                          'piercingLocations',
                          newPiercingLocations,
                        );
                      },
                    },
                  ],
                  typeof values.isGeneralPiercings !== 'boolean'
                    ? []
                    : values.isGeneralPiercings
                    ? [
                        {
                          inputType: 'multi-checkbox',
                          label:
                            'Select which piercing locations you offer for this service:',
                          name: 'piercingLocations',
                          options: piercingLocationsOptions,
                          required: true,
                          isLabelMultiline: true,
                        },
                      ]
                    : [
                        {
                          inputType: 'category-select',
                          label: 'Piercing Location',
                          name: 'piercingLocations[0]' as keyof UpsertBusinessServiceInitValues,
                          options: piercingLocationsOptions,
                          autoWidth: false,
                          placeholder: 'Select piercing location',
                          required: true,
                          p: 1,
                          selectedBackgroundColor: 'rgba(255, 48, 8, 0.08)',
                          selectedCheckColor: theme.palette.primary.main,
                        },
                      ],
                ]
              : [];

          const inputRows: InputRow<UpsertBusinessServiceInitValues>[] = [
            [
              {
                inputType: 'select',
                label: 'Service Category',
                name: 'serviceCategory',
                options: [
                  { label: 'Piercing', value: ServiceCategory.PIERCING },
                  {
                    label: 'Permanent Makeup',
                    value: ServiceCategory.PERMANENT_MAKEUP,
                  },
                  { label: 'Spa', value: ServiceCategory.SPA },
                  { label: 'Other', value: ServiceCategory.OTHER },
                ],
                autoWidth: false,
                disabled: !!serviceToUpdate?._count?.appointments,
                tooltip: TOOLTIP.serviceCategory,
                placeholder: 'Select service category',
                required: true,
              },
            ],
            [
              {
                inputType: 'text',
                label: 'Service Name',
                name: 'name',
                placeholder: 'Enter service name',
                tooltip: TOOLTIP.name,
                required: true,
              },
            ],
            ...piercingRows,
            [
              {
                inputType: 'textarea',
                label: 'Service Description',
                name: 'description',
                placeholder: 'Enter service description',
                tooltip: TOOLTIP.description,
                required: true,
              },
            ],
            [
              {
                inputType: 'multiSelect',
                label: 'Service Providers',
                name: 'employeeIds',
                allText: 'All users',
                options: user?.business?.employees?.map(e => ({
                  label: e.displayName || 'Unnamed service provider',
                  value: e.employeeId,
                })),
                placeholder: 'Select service providers',
                autoWidth: false,
              },
            ],
            [
              {
                inputType: 'number',
                label: 'Default Duration',
                name: 'defaultDurationInMinutes',
                placeholder: 'Enter service duration',
                suffix: 'minutes',
                tooltip: TOOLTIP.defaultDurationInMinutes,
                min: 5,
                required: true,
              },
              {
                inputType: 'number',
                label: 'Default Price',
                name: 'defaultPrice',
                placeholder: 'Enter service price',
                suffix: getBusinessCurrency(user?.business),
                tooltip: TOOLTIP.defaultPrice,
                min: 0,
                step: 0.01,
                required: true,
              },
            ],
            [
              {
                inputType: 'number',
                label: 'Default Deposit Price',
                name: 'defaultDepositPrice',
                placeholder: 'Enter deposit price',
                suffix: getBusinessCurrency(user?.business),
                tooltip: TOOLTIP.defaultDepositPrice,
                min: 0,
                step: 0.01,
              },
            ],
            [
              {
                inputType: 'select',
                label: 'Online Bookings',
                name: 'onlineBookingsBehavior',
                options: [
                  {
                    label: 'Direct Bookings Enabled',
                    value: ONLINE_BOOKINGS_BEHAVIOR.DIRECT_BOOKINGS,
                  },
                  {
                    label: 'Bookable by Requests',
                    value: ONLINE_BOOKINGS_BEHAVIOR.BOOKING_REQUESTS,
                  },
                  {
                    label: 'Not Bookable Online',
                    value: ONLINE_BOOKINGS_BEHAVIOR.NOT_ONLINE,
                  },
                ],
                autoWidth: false,
                tooltip: TOOLTIP.onlineBookingsBehavior,
                placeholder: 'Select an option',
                required: true,
              },
            ],
            [
              {
                inputType: 'select',
                label: 'Enable Check-Ins',
                name: 'enableCheckIn',
                options: [
                  { label: 'Yes', value: true },
                  { label: 'No', value: false },
                ],
                autoWidth: false,
                tooltip: TOOLTIP.enableCheckIn,
                required: true,
                isVisible: !!user?.business?.queueSettings?.enabled,
              },
            ],
            [
              {
                inputType: 'select',
                label: 'Waiver',
                name: 'waiverName',
                isVisible: hasWaiversFeature && !!draftWaivers?.length,
                options:
                  [null, ...(draftWaivers ?? [])]?.map(waiver => ({
                    label: waiver ? waiver.name : 'No waiver',
                    value: waiver ? waiver.name : '',
                  })) ?? [],
                autoWidth: false,
                placeholder: 'Select waiver',
                required: true,
              },
            ],
            [
              {
                inputType: 'number',
                label: 'Minimum Age',
                name: 'minimumAge',
                placeholder: 'Enter minimum age',
                min: 0,
                required: true,
                allowZero: true,
              },
              {
                inputType: 'select',
                label: 'Age Unit',
                name: 'minimumAgeUnit',
                options: [
                  { label: 'Months', value: 'months' },
                  { label: 'Years', value: 'years' },
                ],
                autoWidth: false,
                required: true,
              },
            ],
            [
              {
                inputType: 'select',
                label: 'Minor ID Upload',
                name: 'requireMinorIdentification',
                options: [
                  { label: 'Yes', value: true },
                  { label: 'No', value: false },
                ],
                autoWidth: false,
                required: true,
                tooltip: TOOLTIP.requireMinorIdentification,
                isVisible: isMinorAge,
              },
            ],
            [
              {
                inputType: 'select',
                label: 'Minor Birth Certificate Upload',
                name: 'requireMinorBirthCertificate',
                options: [
                  { label: 'Yes', value: true },
                  { label: 'No', value: false },
                ],
                autoWidth: false,
                required: true,
                tooltip: TOOLTIP.requireMinorBirthCertificate,
                isVisible: isMinorAge,
              },
            ],
            [
              {
                inputType: 'select',
                label: 'Parent/Guardian Identification Upload',
                name: 'requireMinorGuardianIdentification',
                options: [
                  { label: 'Yes', value: true },
                  { label: 'No', value: false },
                ],
                autoWidth: false,
                required: true,
                tooltip: TOOLTIP.requireMinorGuardianIdentification,
                isVisible: isMinorAge,
              },
            ],
            [
              {
                inputType: 'select',
                label: 'Parent/Guardian Present',
                name: 'requireMinorGuardianPresent',
                options: [
                  { label: 'Yes', value: true },
                  { label: 'No', value: false },
                ],
                autoWidth: false,
                required: true,
                tooltip: TOOLTIP.requireMinorGuardianPresent,
                isVisible: isMinorAge,
              },
            ],
            [
              {
                inputType: 'text',
                label: 'Aftercare Link',
                name: 'aftercareLink',
                placeholder: 'Enter aftercare link',
                tooltip: TOOLTIP.aftercareLink,
              },
            ],
            [
              {
                inputType: 'action-button',
                label: 'Customize booking form',
                onClick: () => onCustomizeBookingForm(serviceToUpdate ?? null),
              },
            ],
          ];
          return inputRows;
        },
        onSubmit: async values => {
          const isWaiverPublished = waivers?.some(
            waiver =>
              waiver.name === values.waiverName &&
              waiver.status === 'PUBLISHED',
          );
          const isSelectedWaiverUpdated =
            !serviceToUpdate?.waiver ||
            serviceToUpdate?.waiver?.name !== values.waiverName;

          if (
            values.waiverName &&
            !isWaiverPublished &&
            isSelectedWaiverUpdated
          ) {
            const confirmed = await confirm({
              title: 'Waiver not published',
              message:
                'The waiver you selected has not been published. In order for the selected waiver to be used, it must be published. If you continue, you can publish the waiver later and it will be automatically applied to this service.',
              positiveText: 'Continue',
              negativeText: 'Cancel',
            });
            if (!confirmed) return;
          }

          const isCategoryChanged =
            serviceToUpdate &&
            serviceToUpdate.serviceCategory !== values.serviceCategory;
          if (isCategoryChanged && serviceToUpdate?._count.appointments) return;

          const intakeQuestions = !serviceToUpdate
            ? getPendingIntakeQuestions?.()
            : undefined;

          const response = await handleUpsertBusinessService(
            user.business.id,
            serviceToUpdate ?? null,
            values,
            intakeQuestions,
          );
          if (response) {
            setForm(null);
            postMessageToReactNative('services-changed');
            reloadAfterUpdate('SHOP_SETTINGS');
            reloadUser();
            refetch();
            if (hasWaiversFeature) refetchWaivers();
          }
        },
        submitButtonText: serviceToUpdate ? 'Update Service' : 'Create Service',
        title: serviceToUpdate ? 'Edit Service' : 'Create Service',
        validationSchema: upsertBusinessServiceFormSchema,
      };
    },
    [
      waivers,
      user,
      piercingLocationsOptions,
      hasWaiversFeature,
      theme,
      confirm,
      reloadAfterUpdate,
      reloadUser,
      refetch,
      refetchWaivers,
      setForm,
      onCustomizeBookingForm,
      getPendingIntakeQuestions,
    ],
  );

  return { getServiceForm };
}

export default useServiceForm;
